import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { eventAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { 
  Search, Calendar, MapPin, Users, Loader2, Video, ExternalLink
} from 'lucide-react';

export default function Events() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const type = searchParams.get('type') || '';
  const status = searchParams.get('status') || '';

  useEffect(() => {
    fetchEvents();
  }, [type, status]);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const response = await eventAPI.getAll({ 
        type: type || undefined, 
        status: status || undefined 
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('type', value);
    } else {
      newParams.delete('type');
    }
    setSearchParams(newParams);
  };

  const handleStatusChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('status', value);
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'ongoing': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-slate-100 text-slate-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'hackathon': return 'bg-purple-100 text-purple-700';
      case 'workshop': return 'bg-orange-100 text-orange-700';
      case 'meetup': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="events-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Events</h1>
          <p className="text-slate-600 mt-1">Hackathons, workshops, and meetups in the PDX tech community</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Select value={type || 'all'} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Event Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="hackathon">Hackathons</SelectItem>
              <SelectItem value="workshop">Workshops</SelectItem>
              <SelectItem value="meetup">Meetups</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status || 'all'} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Events Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No events found</h3>
            <p className="text-slate-600">Check back soon for upcoming events!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Card key={event.event_id} className="card-hover overflow-hidden" data-testid={`event-card-${event.event_id}`}>
                {event.image_url && (
                  <div className="aspect-video bg-slate-100">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-5">
                  {/* Badges */}
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={getTypeColor(event.type)}>
                      {event.type}
                    </Badge>
                    <Badge className={getStatusColor(event.status)}>
                      {event.status}
                    </Badge>
                  </div>

                  {/* Title */}
                  <Link to={`/events/${event.event_id}`}>
                    <h3 className="text-lg font-semibold text-slate-900 hover:text-indigo-600 transition-colors mb-2">
                      {event.title}
                    </h3>
                  </Link>

                  {/* Date */}
                  {event.start_date && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {format(new Date(event.start_date), 'MMM d, yyyy')}
                        {event.end_date && event.end_date !== event.start_date && (
                          <> - {format(new Date(event.end_date), 'MMM d, yyyy')}</>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                    {event.is_virtual ? (
                      <>
                        <Video className="w-4 h-4" />
                        <span>Virtual Event</span>
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4" />
                        <span>{event.location || 'TBA'}</span>
                      </>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                    {event.short_description || event.description}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Users className="w-4 h-4" />
                      <span>{event.registration_count || 0} registered</span>
                    </div>
                    <Link to={`/events/${event.event_id}`}>
                      <Button size="sm" variant="outline">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
